/* ============================================================
   Wido Ops — mock data layer.  Attaches window.WidoData.
   Deterministic (seeded) so the prototype is stable across reloads.
   Business rules encoded from "Quy định về việc đi muộn & về sớm":
     - 300 flexible minutes / month (late + early combined)
     - Up to 4 approved permissions / month
     - First 60 min/day of an approved permission is free
     - Overflow hits the fund, then work hours (unpaid)
   ============================================================ */
(function () {
  // ---- tiny seeded RNG (mulberry32) ----
  function rng(seed) {
    return function () {
      seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  const rand = rng(20260608);
  const pick = (arr) => arr[Math.floor(rand() * arr.length)];
  const ri = (a, b) => a + Math.floor(rand() * (b - a + 1));

  const MONTH_LABEL = "June 2026";
  // The "current period" the whole app is anchored to. In API mode bootstrap.js
  // overrides CUR_YEAR/CUR_MONTH (and MONTH_LABEL/DAYS_ELAPSED) from the real
  // clock; screens that need the live period (e.g. calendar month nav) read
  // these off window.WidoData rather than hardcoding their own copy.
  const CUR_YEAR = 2026;
  const CUR_MONTH = 6;
  const FUND_TOTAL = 300;
  const PERMISSIONS_TOTAL = 4;

  // ---- Teams ----
  const TEAMS = [
    { id: "art", name: "Art", color: "#ef5b2e", icon: "palette" },
    { id: "gd", name: "Game Designer", color: "#3a9a45", icon: "stadia_controller" },
    { id: "3d", name: "3D Model", color: "#cf4820", icon: "deployed_code" },
    { id: "unity", name: "Unity", color: "#2a7d36", icon: "view_in_ar" },
    { id: "tester", name: "Tester", color: "#aa3411", icon: "bug_report" },
    { id: "ua", name: "UA", color: "#4caf52", icon: "campaign" },
    { id: "creative", name: "Creative", color: "#ff8a64", icon: "auto_awesome" },
    { id: "pm", name: "PM", color: "#14512a", icon: "checklist" },
    { id: "mediation", name: "Mediation", color: "#2e8b8b", icon: "handshake" },
    { id: "admin", name: "Admin", color: "#7a5c2e", icon: "admin_panel_settings" },
    { id: "animation", name: "Animation", color: "#e07b39", icon: "animation" },
    { id: "data", name: "Data", color: "#357a9e", icon: "database" },
    { id: "leaders", name: "Leaders", color: "#b59410", icon: "workspace_premium" },
    { id: "backend", name: "Backend", color: "#4a6fa5", icon: "dns" },
    { id: "bd", name: "BD", color: "#7b5ea7", icon: "business_center" },
  ];

  // ---- Roles & permissions (RBAC) ----
  const PERMISSIONS = [
    { id: "attendance.view.own", group: "Attendance", label: "View own attendance", desc: "See personal timesheet, fund and quotas." },
    { id: "attendance.view.team", group: "Attendance", label: "View team late records", desc: "See late/early records of managed members." },
    { id: "attendance.view.all", group: "Attendance", label: "View company late records", desc: "See late/early records for everyone." },
    { id: "attendance.override", group: "Attendance", label: "Manual clock override", desc: "Enter missing clock-in / clock-out for staff." },
    { id: "attendance.settings", group: "Attendance", label: "Manage fund settings", desc: "Set default and per-person flexible-minute allowances." },
    { id: "request.create", group: "Requests", label: "Submit requests", desc: "File late-arrival, early-leave and leave requests." },
    { id: "request.approve.team", group: "Requests", label: "Approve team requests", desc: "Approve / reject requests from managed members." },
    { id: "request.approve.all", group: "Requests", label: "Approve any request", desc: "Approve / reject requests company-wide." },
    { id: "request.view.all", group: "Requests", label: "View all requests", desc: "See every request company-wide (read-only; no approval)." },
    { id: "payroll.offset", group: "Payroll", label: "Manage leave offsets", desc: "Apply annual-leave offset before payroll close." },
    { id: "account.activate", group: "People", label: "Activate accounts", desc: "Approve pending SSO sign-ups; deactivate accounts." },
    { id: "user.manage", group: "People", label: "Manage users", desc: "Edit profiles, team and reporting lines." },
    { id: "role.manage", group: "People", label: "Manage roles", desc: "Create roles and assign permissions." },
    { id: "policy.view", group: "Policy", label: "View policies", desc: "Read company rules and regulations." },
    { id: "policy.manage", group: "Policy", label: "Edit policies", desc: "Publish and update policy documents." },
    { id: "meetings.calendar.view.all", group: "Meetings", label: "View all meeting calendars", desc: "See the Google Calendar meetings of all people org-wide on the Meeting rooms page." },
  ];

  const ROLES = [
    {
      id: "employee", name: "Employee", system: true, accent: "#5c5f5b",
      desc: "Default role. Sees only their own data.",
      perms: ["attendance.view.own", "request.create", "policy.view"],
    },
    {
      id: "leader", name: "Team Leader", system: true, accent: "#3a9a45",
      desc: "Manages one or more teams; sees and approves for their people.",
      perms: ["attendance.view.own", "attendance.view.team", "request.create", "request.approve.team", "policy.view"],
    },
    {
      id: "hr", name: "HR", system: true, accent: "#ef5b2e",
      desc: "People operations. Company-wide visibility and overrides.",
      perms: ["attendance.view.own", "attendance.view.all", "attendance.override", "attendance.settings", "request.create",
        "request.view.all", "payroll.offset", "account.activate", "user.manage", "role.manage", "policy.view", "policy.manage"],
    },
    {
      id: "bod", name: "BOD / Admin", system: true, accent: "#14512a",
      desc: "Board of Directors. Full access to every surface.",
      perms: PERMISSIONS.map((p) => p.id),
    },
  ];

  // ---- People ----
  // 3 BOD + 27 staff = 30. Leaders are drawn from staff; a few lead 2 teams.
  const FIRST_BY_TEAM = {};
  const fnames = [
    "Tran Thu Ha", "Le Minh Quan", "Pham Hoang Nam", "Do Gia Bao", "Vu Thi Mai",
    "Bui Anh Tu", "Dang Quoc Huy", "Ngo Bao Chau", "Hoang Thi Lan", "Ly Gia Han",
    "Trinh Cong Son", "Mai Phuong Thao", "Duong Minh Duc", "Cao Thi Nhung", "Ha Tuan Kiet",
    "Nguyen Thi Hong", "Pham Duc Anh", "Le Thi Thu", "Vo Thanh Tung", "Dinh Khanh Vy",
    "Ta Quang Hai", "Luong Bao Ngoc", "Phan Dinh Khoa", "Nguyen Hai Yen", "Tran Quoc Bao",
    "Do Thuy Linh", "Bui Minh Triet",
  ];

  const people = [];
  const HR_PERSON = { id: "u_hr", name: "Nguyen Thi Hong", team: "pm", title: "HR Manager", roleId: "hr" };

  // BOD
  people.push({ id: "u_ceo", name: "Nguyen Duy Khanh", team: null, title: "CEO", roleId: "bod", leads: [], status: "active" });
  people.push({ id: "u_cto", name: "Nguyen Van Tuan", team: "unity", title: "CTO", roleId: "bod", leads: ["unity", "tester"], status: "active" });
  people.push({ id: "u_cmo", name: "Phung Thi Ngoc Lan", team: "ua", title: "CMO", roleId: "bod", leads: ["ua", "creative"], status: "active" });

  // team leaders (staff): Art+3D = Tran Thu Ha; GD+PM = Le Minh Quan; (Unity/Tester led by CTO; UA/Creative by CMO)
  const leaderMap = {
    art: "Tran Thu Ha", "3d": "Tran Thu Ha",
    gd: "Le Minh Quan", pm: "Le Minh Quan",
    unity: "u_cto", tester: "u_cto",
    ua: "u_cmo", creative: "u_cmo",
  };

  let idx = 0;
  function add(name, team, title, roleId, leads, status) {
    const id = "u" + (++idx);
    people.push({ id, name, team, title, roleId: roleId || "employee", leads: leads || [], status: status || "active" });
    return id;
  }

  // staff roster — distribute across teams
  const roster = [
    ["Tran Thu Ha", "art", "Art Lead", "leader", ["art", "3d"]],
    ["Le Minh Quan", "gd", "Game Design Lead", "leader", ["gd", "pm"]],
    ["Pham Hoang Nam", "unity", "Senior Unity Engineer", "employee", []],
    ["Do Gia Bao", "unity", "Unity Engineer", "employee", []],
    ["Vu Thi Mai", "art", "2D Artist", "employee", []],
    ["Bui Anh Tu", "art", "Concept Artist", "employee", []],
    ["Dang Quoc Huy", "3d", "3D Modeler", "employee", []],
    ["Ngo Bao Chau", "3d", "Character Artist", "employee", []],
    ["Hoang Thi Lan", "gd", "Game Designer", "employee", []],
    ["Ly Gia Han", "gd", "Level Designer", "employee", []],
    ["Trinh Cong Son", "unity", "Unity Engineer", "employee", []],
    ["Mai Phuong Thao", "tester", "QA Lead", "employee", []],
    ["Duong Minh Duc", "tester", "QA Tester", "employee", []],
    ["Cao Thi Nhung", "tester", "QA Tester", "employee", []],
    ["Ha Tuan Kiet", "ua", "UA Specialist", "employee", []],
    ["Nguyen Thi Hong", "pm", "HR Manager", "hr", []],
    ["Pham Duc Anh", "ua", "Performance Marketer", "employee", []],
    ["Le Thi Thu", "creative", "Creative Producer", "employee", []],
    ["Vo Thanh Tung", "creative", "Motion Designer", "employee", []],
    ["Dinh Khanh Vy", "creative", "Copywriter", "employee", []],
    ["Ta Quang Hai", "unity", "Build Engineer", "employee", []],
    ["Luong Bao Ngoc", "pm", "Project Manager", "employee", []],
    ["Phan Dinh Khoa", "3d", "Technical Artist", "employee", []],
    ["Nguyen Hai Yen", "art", "UI Artist", "employee", []],
    ["Tran Quoc Bao", "gd", "Systems Designer", "employee", []],
    ["Do Thuy Linh", "ua", "ASO Specialist", "employee", []],
    ["Bui Minh Triet", "tester", "Automation QA", "employee", []],
  ];
  roster.forEach((r) => add(r[0], r[1], r[2], r[3], r[4], "active"));

  // a couple of pending / deactivated accounts for the activation queue
  add("Truong Gia Phong", "unity", "Unity Engineer (new)", "employee", [], "pending");
  add("Le Hoang My", "art", "2D Artist (new)", "employee", [], "pending");
  add("Pham Van Loc", "tester", "QA Tester", "employee", [], "deactivated");

  // ---- Employment history (start dates + rehire tracking) ----
  // periods: [{ from: ISO, to: ISO|null }] — `to === null` means currently employed.
  // A person who left and returned has multiple periods.
  const EMP = {
    u_ceo: [["2019-01-15", null]], u_cto: [["2019-06-01", null]], u_cmo: [["2020-02-01", null]],
    u1: [["2021-03-01", null]],                                  // Tran Thu Ha
    u2: [["2021-05-10", null]],                                  // Le Minh Quan
    u3: [["2020-08-01", null]],                                  // Pham Hoang Nam (senior)
    u4: [["2024-02-12", null]],                                  // Do Gia Bao
    u5: [["2026-03-02", null]],                                  // Vu Thi Mai — joined this year
    u8: [["2022-01-10", "2025-08-31"], ["2026-04-01", null]],    // Ngo Bao Chau — left & rejoined
    u11: [["2023-07-01", null]],
    u18: [["2025-11-03", null]],                                 // Le Thi Thu — joined late last year
    u30: [["2023-05-01", "2026-05-15"]],                          // Pham Van Loc — left (deactivated)
    u28: [["2026-06-05", null]],                                 // Truong Gia Phong (pending) — start on sign-up
    u29: [["2026-06-06", null]],                                 // Le Hoang My (pending)
  };
  const rh = (n) => ["2022-01-01", "2022-09-01", "2023-02-01", "2023-06-01", "2024-01-15", "2024-05-01", "2025-01-10"][n % 7];
  people.forEach((p, i) => {
    if (EMP[p.id]) { p.periods = EMP[p.id].map(([from, to]) => ({ from, to })); }
    else { p.periods = [{ from: rh(i), to: p.status === "deactivated" ? "2026-04-30" : null }]; }
    p.startDate = p.periods[0].from;
  });

  const byId = (id) => people.find((p) => p.id === id);
  const teamName = (id) => (TEAMS.find((t) => t.id === id) || {}).name || "—";
  const roleName = (id) => (ROLES.find((r) => r.id === id) || {}).name || id;

  // ---- Employment helpers ----
  const CURRENT_ISO = "2026-06-30";
  function monthISO(m) { return "2026-" + String(m).padStart(2, "0"); } // year-month prefix
  // Count working months Jan–Jun 2026 the person was employed (for accrual).
  // Annual leave only accrues on/after the official employment start date
  // (person.officialStartDate); when unset, accrue from the first period.
  function monthsEmployed2026(person) {
    // The official employment start date GATES eligibility (probation -> 0); the
    // START DATE sets the amount. A first-year employee (less than a full year
    // since their start date) accrues only up to the CURRENT month; once they've
    // completed a year they accrue the whole year (through December). Joining Apr,
    // official Jun, viewed in Jun -> Apr..Jun = 3.
    const official = (person && person.officialStartDate) || null;
    if (!official) return 0;
    const startYM = (person.startDate) ? person.startDate.slice(0, 7) : null;
    const curMonth = (window.WidoData && window.WidoData.CUR_MONTH) || CUR_MONTH || 12;
    let upper = 12;
    if (person.startDate) {
      const sy = +person.startDate.slice(0, 4), sm = +person.startDate.slice(5, 7);
      const monthsSinceStart = (2026 - sy) * 12 + (curMonth - sm);
      if (monthsSinceStart < 12) upper = Math.max(1, Math.min(12, curMonth));
    }
    let n = 0;
    for (let m = 1; m <= upper; m++) {
      const ym = "2026-" + String(m).padStart(2, "0");
      // Prefer explicit periods when present (bounds leaving early); else count
      // from the start date through the upper bound.
      const employed = (person.periods && person.periods.length)
        ? person.periods.some((pr) => pr.from.slice(0, 7) <= ym && (pr.to == null || pr.to.slice(0, 7) >= ym))
        : (!startYM || startYM <= ym);
      if (employed) n++;
    }
    return n;
  }
  function isEmployed(person) { return person && person.periods && person.periods.some((pr) => pr.to == null); }
  function currentStart(person) {
    if (!person || !person.periods || !person.periods.length) return null;
    const open = person.periods.find((pr) => pr.to == null);
    return open ? open.from : person.periods[person.periods.length - 1].from;
  }
  function tenureLabel(person) {
    const s = currentStart(person); if (!s) return "—";
    const start = new Date(s + "T00:00:00"), now = new Date("2026-06-30T00:00:00");
    let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    if (months < 0) months = 0;
    const y = Math.floor(months / 12), mo = months % 12;
    return (y ? y + "y " : "") + mo + "mo";
  }
  function fmtDate(iso) { if (!iso) return "—"; return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  // A request's date as a human label. Annual leave can span a range and cover
  // half days, so show the portion: "· Morning"/"· Afternoon" on a single day,
  // or "Jun 23 (PM) – Jun 25 (AM)" across a range. Every other type (and a plain
  // full single day) is just its date. Mock rows carry a label string and no
  // range/portion fields, so they fall through to the bare date untouched.
  function reqDateLabel(r) {
    if (!r) return "—";
    const fmt = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d) ? fmtDate(d) : d;
    const T = (s) => (window.t ? window.t(s) : s);
    const from = r.date;
    const to = (r.dateTo && r.dateTo > from) ? r.dateTo : null;
    const sp = r.startPortion || "full";
    const ep = r.endPortion || "full";
    if (!to) {
      if ((r.type === "leave" || r.type === "unpaid_leave") && sp !== "full") return fmt(from) + " · " + T(sp === "pm" ? "Afternoon" : "Morning");
      return fmt(from);
    }
    return fmt(from) + (sp === "pm" ? " (PM)" : "") + " – " + fmt(to) + (ep === "am" ? " (AM)" : "");
  }

  // ---- Fund / permission allowance overrides + daily grants ----
  // Some staff have a different monthly flexible-minute allowance than the
  // company default (e.g. part-time, probation, negotiated). Others get a
  // DAILY free-minute grant over a date range (e.g. nursing a child under 1)
  // that is consumed BEFORE the monthly fund. Stored as:
  //   { defaultMinutes, defaultPerms,
  //     people: { id: {minutes?, perms?, note?} },
  //     daily:  [ {id, personId, minutesPerDay, from, to, reason} ] }
  const DEFAULT_SETTINGS = {
    // System integration config (Google Sheets export target for the Hours
    // report). Server-authoritative — comes back on GET /settings as `system`;
    // this is just the empty fallback before the backend responds.
    system: { sheetUrl: "", sheetName: "", sheetTab: "" },
    defaultMinutes: FUND_TOTAL,
    defaultPerms: PERMISSIONS_TOTAL,
    defaultAnnual: 12,          // annual-leave days per year (1 / working month)
    defaultShift: { amStart: "08:00", amEnd: "12:00", pmStart: "13:30", pmEnd: "17:30" },
    schedules: [               // working-hours overrides by date range (e.g. winter hours)
      { id: "sc1", from: "2025-12-01", to: "2026-02-28", amStart: "09:00", amEnd: "12:00", pmStart: "13:30", pmEnd: "18:00", label: "Winter hours" },
    ],
    leaveBackfill: {            // annual-leave taken Jan–May 2026 (pre-policy) — HR-approved entries
      u4: [{ id: "bf1", date: "2026-02-10", days: 1, note: "Tet extension" }, { id: "bf2", date: "2026-04-18", days: 1, note: "Personal day" }],
      u1: [{ id: "bf3", date: "2026-01-20", days: 1, note: "Family" }, { id: "bf4", date: "2026-03-05", days: 2, note: "Trip" }],
      u3: [{ id: "bf5", date: "2026-05-02", days: 1, note: "Personal day" }],
      u11: [{ id: "bf6", date: "2026-02-03", days: 2, note: "Medical" }, { id: "bf7", date: "2026-04-28", days: 2, note: "Family" }],
    },
    leaveBonus: {               // year-scoped bonus days — extra leave for ONE year, then expires
      u1: [{ id: "bn1", year: 2026, days: 2, note: "Project crunch bonus" }],
    },
    devices: [                  // face-recognition terminals (each assigns its own employee IDs)
      { id: "dev1", name: "Front lobby", location: "Floor 1 entrance" },
      { id: "dev2", name: "Studio floor", location: "Floor 3" },
      { id: "dev3", name: "Back entrance", location: "Parking" },
    ],
    // id + name auto-fetched from each terminal (the backend syncs these). HR only maps
    // each entry to a Wido Ops employee — the device-reported name is just a hint.
    deviceEnrollments: {
      dev1: [
        { extId: "1042", name: "Bao Do" }, { extId: "1001", name: "Ha Tran" },
        { extId: "1108", name: "Nam Pham" }, { extId: "1055", name: "Mai Vu" },
        { extId: "1077", name: "Tu B." }, { extId: "1090", name: "New Enrollment" },
      ],
      dev2: [
        { extId: "A-17", name: "Bao D." }, { extId: "A-02", name: "Ha T." },
        { extId: "A-31", name: "Mai V." }, { extId: "A-44", name: "Duc Duong" },
      ],
      dev3: [
        { extId: "B-05", name: "Ha Tran" }, { extId: "B-99", name: "Guest" },
      ],
    },
    faceMap: {                  // personId → { deviceId: external employee id on that device }
      u4: { dev1: "1042", dev2: "A-17" },
      u1: { dev1: "1001", dev2: "A-02", dev3: "B-05" },
      u3: { dev1: "1108" },
      u5: { dev1: "1055", dev2: "A-31" },
      u11: { dev2: "A-44" },
    },
    people: {
      u3: { minutes: 450, annual: 18, note: "Senior — negotiated larger fund & leave" }, // Pham Hoang Nam
      u1: { annual: 15, note: "Team lead — seniority leave" },                            // Tran Thu Ha
      u13: { minutes: 150, perms: 2, note: "Part-time (3 days/week)" },                   // Duong Minh Duc
      u20: { minutes: 240, note: "Probation — reduced allowance" },                        // Dinh Khanh Vy
    },
    daily: [
      { id: "d1", personId: "u5", minutesPerDay: 60, from: "2026-06-01", to: "2026-12-31", reason: "Raising a child under 1 — nursing time" }, // Vu Thi Mai
      { id: "d2", personId: "u18", minutesPerDay: 45, from: "2026-05-15", to: "2026-08-15", reason: "Raising a child under 1 — nursing time" }, // Le Thi Thu
    ],
    // Company holidays. recurring=true repeats every year on (month, day);
    // recurring=false is a specific date (or date..dateTo range) for one year
    // (e.g. Lunar New Year, whose Gregorian dates move). Non-working + excused.
    holidays: [
      { id: "h_newyear", name: "New Year's Day", recurring: true, month: 1, day: 1 },
      { id: "h_reunification", name: "Reunification Day", recurring: true, month: 4, day: 30 },
      { id: "h_workers", name: "International Workers' Day", recurring: true, month: 5, day: 1 },
      { id: "h_national", name: "National Day", recurring: true, month: 9, day: 2 },
    ],
    // Compensation working days: a (weekend) workDate that becomes a working day,
    // with the optional offDate (a working day taken off in exchange).
    compWorkdays: [],
  };
  function effMinutes(id, settings) {
    const s = settings || DEFAULT_SETTINGS;
    const pp = (s.people || {})[id] || {};
    return pp.minutes != null ? pp.minutes : (s.defaultMinutes != null ? s.defaultMinutes : FUND_TOTAL);
  }
  function effPerms(id, settings) {
    const s = settings || DEFAULT_SETTINGS;
    const pp = (s.people || {})[id] || {};
    return pp.perms != null ? pp.perms : (s.defaultPerms != null ? s.defaultPerms : PERMISSIONS_TOTAL);
  }
  function isOverridden(id, settings) {
    const s = settings || DEFAULT_SETTINGS;
    const pp = (s.people || {})[id];
    return !!(pp && (pp.minutes != null || pp.perms != null || pp.annual != null));
  }
  // ---- Annual leave: 1 day accrued per working month, pro-rated to entitlement ----
  const MONTHS_ELAPSED = 6; // Jan–Jun 2026 inclusive (current month = June)
  function effAnnual(id, settings) {
    const s = settings || DEFAULT_SETTINGS;
    const pp = (s.people || {})[id] || {};
    return pp.annual != null ? pp.annual : (s.defaultAnnual != null ? s.defaultAnnual : 12);
  }
  function isAnnualCustom(id, settings) {
    const pp = ((settings || DEFAULT_SETTINGS).people || {})[id];
    return !!(pp && pp.annual != null);
  }
  // Days accrued so far this year: 1 / working month for the standard 12; pro-rated
  // (entitlement / 12 per month) so a higher entitlement accrues proportionally faster.
  // Accrues only for months the person was actually employed.
  function annualAccrued(id, settings, monthsElapsed) {
    const ent = effAnnual(id, settings);
    const m = monthsElapsed != null ? monthsElapsed : MONTHS_ELAPSED;
    return Math.min(ent, Math.round((ent / 12) * m));
  }
  // { entitlement, accrued, taken, available, backfill, months } as of the current month.
  // `currentTaken` = days taken in the live (June+) policy window; backfill = Jan–May 2026 manual entry.
  // Annual-leave taken Jan–May 2026 — supports a legacy number or HR-approved entries list.
  function backfillEntries(id, settings) {
    const v = ((settings || DEFAULT_SETTINGS).leaveBackfill || {})[id];
    if (v == null) return [];
    if (typeof v === "number") return v > 0 ? [{ id: "legacy", date: "2026-01-01", days: v, note: "Imported balance" }] : [];
    return v;
  }
  function backfillDays(id, settings) {
    const v = ((settings || DEFAULT_SETTINGS).leaveBackfill || {})[id];
    if (v == null) return 0;
    if (typeof v === "number") return v;
    return v.reduce((a, e) => a + (+e.days || 0), 0);
  }
  // Year-scoped bonus days for `id` in `year` (default: the current year). Extra
  // leave granted for ONE year only — a 2026 bonus adds to 2026's balance and is
  // gone once 2027 is computed. Mirrors bonus_days in the backend attendance svc.
  function bonusDays(id, settings, year) {
    const entries = ((settings || DEFAULT_SETTINGS).leaveBonus || {})[id] || [];
    const y = year != null ? year : CUR_YEAR;
    return entries.reduce((a, e) => a + (+e.year === y ? (+e.days || 0) : 0), 0);
  }
  function computeLeave(id, settings, currentTaken, person) {
    const entitlement = effAnnual(id, settings);
    const p = person || byId(id);
    const months = p ? monthsEmployed2026(p) : MONTHS_ELAPSED;
    const accrued = Math.min(entitlement, Math.round((entitlement / 12) * months));
    const backfill = backfillDays(id, settings);
    // Bonus is added ON TOP of the accrued entitlement for the current year.
    const bonus = bonusDays(id, settings, CUR_YEAR);
    const taken = (currentTaken || 0) + backfill;
    return { entitlement, accrued, taken, backfill, bonus, currentTaken: currentTaken || 0, months,
      available: Math.max(0, accrued + bonus - taken), custom: isAnnualCustom(id, settings) };
  }
  // ISO date for a June-2026 working-day number.
  function dayISO(day) { return "2026-06-" + String(day).padStart(2, "0"); }
  // Active daily grants for a person on a given ISO date (max minutes wins).
  function dailyGrantForISO(id, iso, settings) {
    const s = settings || DEFAULT_SETTINGS;
    let best = 0;
    (s.daily || []).forEach((g) => {
      if (g.personId === id && iso >= g.from && iso <= g.to) best = Math.max(best, g.minutesPerDay);
    });
    return best;
  }
  function dailyGrantFor(id, day, settings) { return dailyGrantForISO(id, dayISO(day), settings); }
  // Does a person have any daily grant active within the visible month?
  function dailyActive(id, settings) {
    const s = settings || DEFAULT_SETTINGS;
    return (s.daily || []).some((g) => g.personId === id && g.from <= "2026-06-30" && g.to >= "2026-06-01");
  }
  // Daily grants (with their reason) active for a person during a given month —
  // drives the "why" shown in the deduction breakdown.
  function dailyGrantsFor(id, settings, year, month) {
    const s = settings || DEFAULT_SETTINGS;
    const y = year || 2026, mo = month || 6;
    const ms = y + "-" + String(mo).padStart(2, "0") + "-01";
    const me = y + "-" + String(mo).padStart(2, "0") + "-31";
    return (s.daily || []).filter((g) => g.personId === id && g.from <= me && g.to >= ms);
  }

  // ---- Working-hours schedule (date-range shift overrides) ----
  // Normalize a shift record (supports legacy {start,end} → single afternoon-less form).
  function normShift(o, fallback) {
    if (!o) return fallback;
    // Coerce blank/malformed clock fields (e.g. an am_end/pm_start left empty in
    // the Working Calendar) to the fallback so a shift never carries a non-"HH:MM"
    // value that would make the timesheet math produce NaN.
    const hhmm = (v, def) => (typeof v === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(v.trim()) ? v.trim() : def);
    if (hhmm(o.amStart, "")) return {
      amStart: hhmm(o.amStart, fallback.amStart), amEnd: hhmm(o.amEnd, fallback.amEnd),
      pmStart: hhmm(o.pmStart, fallback.pmStart), pmEnd: hhmm(o.pmEnd, fallback.pmEnd),
    };
    return { amStart: hhmm(o.start, fallback.amStart), amEnd: fallback.amEnd, pmStart: fallback.pmStart, pmEnd: hhmm(o.end, fallback.pmEnd) };
  }
  // Parse loose keyboard input into a canonical 24-hour "HH:MM", or null if it
  // can't. Powers the schedule editor's 24h clock fields (native <input
  // type="time"> renders AM/PM on 12-hour-locale browsers and can't be forced to
  // 24h per-input, so those use a plain text field validated through this).
  // Accepts "8", "8:5", "08:30", "830", "0830", "18:00".
  function parseHHMM(raw) {
    const s = String(raw == null ? "" : raw).trim();
    if (!s) return null;
    let h, m, mm;
    if ((mm = s.match(/^(\d{1,2}):(\d{1,2})$/))) { h = +mm[1]; m = +mm[2]; }
    else if (/^\d{3,4}$/.test(s)) { h = +s.slice(0, s.length - 2); m = +s.slice(-2); }
    else if (/^\d{1,2}$/.test(s)) { h = +s; m = 0; }
    else return null;
    if (h > 23 || m > 59) return null;
    return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");
  }
  function shiftFor(iso, settings) {
    const s = settings || DEFAULT_SETTINGS;
    const def = normShift(s.defaultShift, { amStart: "08:00", amEnd: "12:00", pmStart: "13:30", pmEnd: "17:30" });
    const sc = (s.schedules || []).find((x) => iso >= x.from && (!x.to || iso <= x.to));
    if (sc) return Object.assign(normShift(sc, def), { label: sc.label || "Custom hours", custom: true });
    return Object.assign({}, def, { label: "Standard hours", custom: false });
  }
  function shiftForMonth(year, month, settings) {
    return shiftFor(year + "-" + String(month).padStart(2, "0") + "-15", settings);
  }
  // Working minutes away for a hourly-leave gap (clock-out → clock-back-in),
  // clamped to [amStart, pmEnd] with the lunch break removed. Mirrors the backend
  // hourly_leave_minutes(); used for the request form's estimated-impact preview.
  function hmToMin(s) { if (!s || s === "—" || s.indexOf(":") < 0) return null; const [h, m] = s.split(":"); return (+h) * 60 + (+m); }
  function hourlyLeaveMinutes(out, back, shift) {
    const sh = shift || { amStart: "08:00", amEnd: "12:00", pmStart: "13:30", pmEnd: "17:30" };
    const o0 = hmToMin(out), b0 = hmToMin(back);
    if (o0 == null || b0 == null) return 0;
    const o = Math.max(o0, hmToMin(sh.amStart)), b = Math.min(b0, hmToMin(sh.pmEnd));
    if (b <= o) return 0;
    const lunch = Math.max(0, Math.min(b, hmToMin(sh.pmStart)) - Math.max(o, hmToMin(sh.amEnd)));
    return Math.max(0, (b - o) - lunch);
  }
  // Every working schedule in effect during a month: each override that overlaps
  // it (clipped to the month, sorted by start) plus the standard hours that apply
  // on the days no override covers. Drives the timesheet's schedule list.
  function schedulesForMonth(year, month, settings) {
    const s = settings || DEFAULT_SETTINGS;
    const p2 = (n) => String(n).padStart(2, "0");
    const mStart = year + "-" + p2(month) + "-01";
    const mEnd = year + "-" + p2(month) + "-" + p2(new Date(year, month, 0).getDate());
    const def = normShift(s.defaultShift, { amStart: "08:00", amEnd: "12:00", pmStart: "13:30", pmEnd: "17:30" });
    const overrides = (s.schedules || [])
      .filter((x) => x.from <= mEnd && (!x.to || x.to >= mStart))
      .map((x) => Object.assign(normShift(x, def), {
        label: x.label || "Custom hours", custom: true,
        from: x.from < mStart ? mStart : x.from,
        to: (!x.to || x.to > mEnd) ? mEnd : x.to,
      }))
      .sort((a, b) => a.from.localeCompare(b.from));
    // Does any day of the month fall outside every override? If so the standard
    // hours apply for the remainder.
    let covered = true;
    const last = new Date(year, month, 0).getDate();
    for (let d = 1; d <= last && covered; d++) {
      const iso = year + "-" + p2(month) + "-" + p2(d);
      if (!overrides.some((o) => iso >= o.from && iso <= o.to)) covered = false;
    }
    const list = overrides.slice();
    if (!covered || overrides.length === 0) {
      list.push(Object.assign({}, def, { label: "Standard hours", custom: false, from: mStart, to: mEnd, isDefault: true }));
    }
    return list;
  }

  // ---- Historical timesheets (previous months / years) ----
  const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  function strHash(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
  // Generic per-month waterfall keyed on real ISO dates (so daily grants resolve by month).
  // Per-component deduction waterfall — the exact mirror of the backend resolve_day.
  // Each absence type (late/early/hourly) is tiered by HOW it was covered, so a
  // permission spent on one request never excuses an UNREQUESTED late on the day:
  //   perm (permTypes) -> free 60 then fund 60; req (reqTypes) -> fund 60; none -> fund 30.
  // Falls back to the per-day permission/hasRequest flags for mock/history records
  // that don't carry the per-type arrays (reproduces the old per-day behaviour).
  function resolveDay(r, dailyGrant, fundLeft) {
    const late = r.lateMin || 0, early = r.earlyMin || 0, hourly = r.hourlyLeaveMin || 0;
    const hasTypes = Array.isArray(r.permTypes) || Array.isArray(r.reqTypes);
    const pt = r.permTypes || [], rt = r.reqTypes || [];
    const tierOf = (typ) => hasTypes
      ? (pt.indexOf(typ) >= 0 ? "perm" : (rt.indexOf(typ) >= 0 ? "req" : "none"))
      : (r.permission ? "perm" : (r.hasRequest ? "req" : "none"));
    const comps = [];
    [["late", late], ["early", early], ["hourly_leave", hourly]].forEach((p) => { if (p[1] > 0) comps.push([p[1], tierOf(p[0])]); });
    const total = late + early + hourly;
    const daily = Math.min(dailyGrant || 0, total); let dleft = daily;
    ["none", "req", "perm"].forEach((want) => comps.forEach((c) => { if (dleft > 0 && c[1] === want) { const t = Math.min(c[0], dleft); c[0] -= t; dleft -= t; } }));
    let free = 0, fund = 0, freeBudget = 60;
    comps.forEach((c) => { if (c[1] === "perm" && freeBudget > 0 && c[0] > 0) { const t = Math.min(c[0], freeBudget); free += t; c[0] -= t; freeBudget -= t; } });
    let prCap = 60;
    comps.forEach((c) => { if ((c[1] === "perm" || c[1] === "req") && c[0] > 0 && prCap > 0 && fundLeft > 0) { const t = Math.min(c[0], prCap, fundLeft); fund += t; c[0] -= t; prCap -= t; fundLeft -= t; } });
    let noneCap = 30;
    comps.forEach((c) => { if (c[1] === "none" && c[0] > 0 && noneCap > 0 && fundLeft > 0) { const t = Math.min(c[0], noneCap, fundLeft); fund += t; c[0] -= t; noneCap -= t; fundLeft -= t; } });
    const work = comps.reduce((s, c) => s + c[0], 0);
    return { daily: daily, free: free, fund: fund, work: work };
  }

  function computeMonthISO(records, personId, settings, year, month) {
    const fundTotal = effMinutes(personId, settings), permTotal = effPerms(personId, settings);
    let fundLeft = fundTotal, fundUsed = 0, workTotal = 0, dailyTotal = 0, permUsed = 0, lateTotal = 0, lateDays = 0;
    const out = records.map((r) => {
      if (r.weekend) return r;
      const iso = year + "-" + String(month).padStart(2, "0") + "-" + String(r.day).padStart(2, "0");
      const grant = dailyGrantForISO(personId, iso, settings);
      lateTotal += r.lateMin || 0; if (r.lateMin > 0) lateDays++;
      // One slot per permission-covered type (two permissions in a day cost two);
      // legacy/mock records without permTypes fall back to the per-day flag.
      if (Array.isArray(r.permTypes)) permUsed += r.permTypes.length; else if (r.permission) permUsed++;
      const res = resolveDay(r, grant, fundLeft);
      fundLeft -= res.fund; fundUsed += res.fund; workTotal += res.work; dailyTotal += res.daily;
      return Object.assign({}, r, { dailyHit: res.daily, freeHit: res.free, fundHit: res.fund, workHit: res.work });
    });
    return { records: out, fundTotal, fundUsed, fundRemaining: Math.max(0, fundTotal - fundUsed), permTotal, permUsed,
      permRemaining: Math.max(0, permTotal - permUsed), workMinDeducted: workTotal, dailyUsed: dailyTotal, lateMinTotal: lateTotal, lateDays };
  }
  function genHistRecords(personId, year, month) {
    const r = rng(strHash(personId + "|" + year + "|" + month));
    const dim = new Date(year, month, 0).getDate();
    const out = [];
    for (let d = 1; d <= dim; d++) {
      const iso = year + "-" + String(month).padStart(2, "0") + "-" + String(d).padStart(2, "0");
      if (!isWorkingDate(iso, DEFAULT_SETTINGS)) { const lbl = dayOffLabel(iso, DEFAULT_SETTINGS); const w = { day: d, weekend: true }; if (lbl) w.holiday = lbl; out.push(w); continue; }
      const rec = { day: d, in: "08:00", out: "17:30", lateMin: 0, earlyMin: 0, status: "on-time", source: "auto", permission: false };
      if (r() < 0.07) {
        // off in the morning, comes in for the afternoon — late counted vs the afternoon start (13:30)
        rec.amOff = true; rec.session = "pm";
        const late = Math.round((r() * 25) / 5) * 5;            // 0–25 min late vs afternoon start
        rec.lateMin = late;
        const t = 13 * 60 + 30 + late;
        rec.in = "—"; rec.pmIn = String(Math.floor(t / 60)).padStart(2, "0") + ":" + String(t % 60).padStart(2, "0");
        rec.status = late ? "am-off+late" : "am-off";
        if (r() < 0.5) rec.permission = true;
        if (r() < 0.05) rec.source = "manual";
        out.push(rec); continue;
      }
      if (r() < 0.32) { const late = Math.round((5 + r() * 50) / 5) * 5; rec.lateMin = late; rec.in = String(8 + Math.floor(late / 60)).padStart(2, "0") + ":" + String(late % 60).padStart(2, "0"); rec.status = "late"; }
      if (r() < 0.12) { const early = Math.round((10 + r() * 45) / 5) * 5; rec.earlyMin = early; const tot = 17 * 60 + 30 - early; rec.out = String(Math.floor(tot / 60)).padStart(2, "0") + ":" + String(tot % 60).padStart(2, "0"); rec.status = rec.status === "on-time" ? "early-leave" : "late+early"; }
      if ((rec.lateMin + rec.earlyMin) > 0 && r() < 0.4) rec.permission = true;
      if (r() < 0.05) rec.source = "manual";
      out.push(rec);
    }
    return out;
  }
  // Unified timesheet month: real data for Jun 2026, seeded history otherwise.
  // Returns computeMonth-shaped result + { year, month, label, notEmployed }.
  function timesheetMonth(personId, year, month, settings) {
    const label = MONTH_NAMES[month - 1] + " " + year;
    const person = byId(personId);
    const ms = year + "-" + String(month).padStart(2, "0") + "-01", me = year + "-" + String(month).padStart(2, "0") + "-28";
    const employed = (person && person.periods) ? person.periods.some((pr) => pr.from <= me && (pr.to == null || pr.to >= ms)) : true;
    if (!employed) return { records: [], notEmployed: true, year, month, label, fundTotal: effMinutes(personId, settings), fundRemaining: effMinutes(personId, settings), permTotal: effPerms(personId, settings), permRemaining: effPerms(personId, settings), workMinDeducted: 0, dailyUsed: 0, lateMinTotal: 0, lateDays: 0 };
    // "Live" = the current period as seen by whoever set CUR_* — data.js in mock
    // mode (June 2026), bootstrap.js from the real clock in API mode. For that
    // month we read the bootstrap-mutated backend data; other months fall back to
    // the mock history generator.
    const W = window.WidoData || {};
    const isLive = year === (W.CUR_YEAR || 2026) && month === (W.CUR_MONTH || 6);
    let raw, srcOt = 0, srcLeave = null, srcMonthLeave = null, srcOffMin = 0, srcOffDays = 0;
    if (isLive) {
      // Read the LIVE bootstrap-mutated data off window.WidoData, not the
      // module-scope closure consts (which stay the mock genMonth output — the
      // bootstrap reassigns the D.* properties, not these bindings).
      const me_ = W.ME || ME, mm = W.meMonth || meMonth, cs = W.companyStats || companyStats;
      const src = personId === me_.id ? mm : (cs.find((s) => s.person.id === personId) || {});
      raw = src.records || mm.records;
      srcOt = src.overtimeMin || 0;                                  // weekend OT (backend)
      srcLeave = (src.leaveDays != null) ? src.leaveDays : (mm.leaveDays || 0);      // cumulative (balance)
      srcMonthLeave = (src.monthLeaveDays != null) ? src.monthLeaveDays : null;      // month-scoped (paidLeave)
      srcOffMin = src.offsetMin || 0;                                // leave-offset minutes (backend)
      srcOffDays = src.offsetDays || 0;                              // annual leave it cost
    } else {
      raw = genHistRecords(personId, year, month);
    }
    const m = computeMonthISO(raw, personId, settings, year, month);
    return Object.assign(m, { year, month, label, current: isLive,
      overtimeMin: srcOt, leaveDays: srcLeave != null ? srcLeave : (m.leaveDays || 0),
      // MONTH-scoped leave taken (feeds monthMetrics paidLeaveMin); falls back to the
      // month figure for mock history. `leaveDays` above stays CUMULATIVE (balance).
      monthLeaveDays: srcMonthLeave != null ? srcMonthLeave : (m.leaveDays || 0),
      offsetMin: srcOffMin, offsetDays: srcOffDays });
  }

  // Central per-month waterfall. Re-runs the deduction order from raw per-day
  // absence so overrides + daily grants flow consistently everywhere:
  //   daily grant → permission-free 60/day → monthly fund → unpaid work hours.
  // With no daily grant and the default 300 fund this reproduces genMonth exactly.
  function computeMonth(rawRecords, personId, settings) {
    const fundTotal = effMinutes(personId, settings);
    const permTotal = effPerms(personId, settings);
    let fundLeft = fundTotal, fundUsed = 0, workTotal = 0, dailyTotal = 0, permUsed = 0, lateTotal = 0, lateDays = 0;
    const records = (rawRecords || []).map((r) => {
      if (r.weekend) return r;
      const grant = dailyGrantFor(personId, r.day, settings);
      lateTotal += r.lateMin || 0; if (r.lateMin > 0) lateDays++;
      // One slot per permission-covered type (two permissions in a day cost two);
      // legacy/mock records without permTypes fall back to the per-day flag.
      if (Array.isArray(r.permTypes)) permUsed += r.permTypes.length; else if (r.permission) permUsed++;
      const res = resolveDay(r, grant, fundLeft);
      fundLeft -= res.fund; fundUsed += res.fund; workTotal += res.work; dailyTotal += res.daily;
      return { ...r, dailyHit: res.daily, freeHit: res.free, fundHit: res.fund, workHit: res.work };
    });
    return {
      records, fundTotal, fundUsed, fundRemaining: Math.max(0, fundTotal - fundUsed),
      permTotal, permUsed, permRemaining: Math.max(0, permTotal - permUsed),
      workMinDeducted: workTotal, dailyUsed: dailyTotal, lateMinTotal: lateTotal, lateDays,
    };
  }

  // Paid-time "Hours summary" for one month (the dashboard + employee-detail card).
  // Derived from the already-computed `month` (records + leaveDays + workMinDeducted
  // + backend-provided overtimeMin). All *Min are minutes.
  //
  // The standard working day is a FIXED 8h (480 min) — NOT the actual shift length
  // (which may sum to e.g. 7h45m) — used for every day<->hour conversion (paid-leave
  // hours, paid-hour day equivalents), mirroring the backend WORKDAY_MINUTES.
  function monthMetrics(month, settings) {
    const s = settings || DEFAULT_SETTINGS;
    const DAY = 480;
    const year = month.year || CUR_YEAR, mo = month.month || CUR_MONTH;
    const dim = new Date(year, mo, 0).getDate();
    const recByDay = {}; (month.records || []).forEach((r) => { recByDay[r.day] = r; });

    let standardDays = 0, holidayMin = 0, workedMin = 0, lunchDays = 0;
    for (let d = 1; d <= dim; d++) {
      const iso = year + "-" + String(mo).padStart(2, "0") + "-" + String(d).padStart(2, "0");
      const wd = new Date(year, mo - 1, d).getDay();           // 0 Sun … 6 Sat
      const isWeekend = wd === 0 || wd === 6;
      const comp = isCompWork(iso, s);
      if (isWeekend && !comp) continue;                        // weekends -> OT, not standard
      // A compensation OFF day is swapped off — its hours are worked on the paired
      // comp WORKING day instead — so it's NOT a standard day and NOT a paid holiday;
      // the comp-work day already stands in for it. Skip BEFORE standardDays so the
      // swap nets out (mirrors the backend month_metrics).
      if (isCompOff(iso, s) && !comp) continue;
      // Excused (maternity / extended absence): not a working day for this person,
      // so it counts nowhere — no standard, paid or unpaid. Excluded BEFORE
      // standardDays, mirroring the backend month_metrics.
      if ((recByDay[d] || {}).excused) continue;
      standardDays++;                                          // weekday / holiday / comp-work = standard paid
      if (holidayOn(iso, s) && !comp) { holidayMin += DAY; continue; } // paid holiday, not worked
      const rec = recByDay[d];
      if (!rec || rec.weekend) continue;                       // future / no data yet
      if (!(rec.in && rec.in !== "—")) continue;               // absent or full-day leave -> 0 worked
      // Scheduled present time after any half-day off (leave or absence carves a
      // session). late/early/hourly are NOT deducted here — those are tracked
      // separately as Unpaid hours — so Actual work hours = scheduled presence.
      const sched = DAY - (rec.amOff ? DAY / 2 : 0) - (rec.pmOff ? DAY / 2 : 0);
      workedMin += sched;
      // Lunch eligibility: present for BOTH sessions (leave overrides check-in);
      // late/early/hourly do NOT disqualify.
      if (!rec.amOff && !rec.pmOff) lunchDays++;
    }

    const overtimeMin = month.overtimeMin || 0;                // weekend work (backend, from timekeeping)
    // An approved leave-offset spends annual leave to cover unpaid minutes, so those
    // minutes count as PAID LEAVE: fold the offset into paidLeaveMin and net it out
    // of Unpaid. The balance deduction itself is in computeLeave.
    const offsetMin = Math.min(month.offsetMin || 0, month.workMinDeducted || 0);
    // MONTH-scoped leave taken this month. NOT month.leaveDays, which is CUMULATIVE
    // (window->month) for the balance and would re-count earlier months' leave here.
    const baseLeaveDays = (month.monthLeaveDays != null) ? month.monthLeaveDays : (month.leaveDays || 0); // annual-leave days taken OFF
    const paidLeaveMin = baseLeaveDays * DAY + offsetMin;      // days off + offset leave hours
    const leaveDays = paidLeaveMin / DAY;                      // total leave-days equiv (incl offset)
    const unpaidMin = Math.max(0, (month.workMinDeducted || 0) - offsetMin);  // late/early/hourly net of offset
    const standardMin = standardDays * DAY;
    // Paid = presence MINUS the RAW late/early/hourly that fell to work, plus paid
    // leave (which now includes the offset), holidays and overtime. Using RAW unpaid
    // here (not the offset-netted figure) keeps the offset from being counted twice.
    const paidMin = Math.max(0, workedMin - (month.workMinDeducted || 0) + paidLeaveMin + holidayMin + overtimeMin);
    return {
      standardMin, standardDays,
      actualWorkMin: workedMin,
      overtimeMin,
      paidLeaveMin, leaveDays,
      holidayMin,
      unpaidMin, offsetMin,
      paidMin, paidDays: paidMin / DAY,
      lunchDays,
    };
  }

  // Re-base a precomputed companyStats row onto effective allowances + daily grants.
  function adjStat(stat, settings) {
    const m = computeMonth(stat.records, stat.person.id, settings);
    return {
      person: stat.person, records: m.records,
      lateMinTotal: m.lateMinTotal, lateDays: m.lateDays,
      fundTotal: m.fundTotal, fundRemaining: m.fundRemaining,
      permTotal: m.permTotal, permRemaining: m.permRemaining,
      workMinDeducted: m.workMinDeducted, dailyUsed: m.dailyUsed,
      leaveDays: stat.leaveDays,                    // cumulative (balance)
      monthLeaveDays: stat.monthLeaveDays,          // month-scoped (paidLeaveMin)
      // Leave-offset (backend) — minutes covered + the leave days they cost. Kept
      // so every consumer can show unpaid NET of offset and the balance hit.
      offsetMin: stat.offsetMin || 0,
      offsetDays: stat.offsetDays || 0,
      overtimeMin: stat.overtimeMin || 0,
      overridden: isOverridden(stat.person.id, settings),
      hasDaily: dailyActive(stat.person.id, settings),
    };
  }

  // resolve leader display for a person — derive from the live org structure
  // (the active person whose `leads` list includes this person's team). Falls
  // back to the static leaderMap only when no live leader is found. `people` is
  // mutated in place by the bootstrap, so this resolves live data in API mode.
  function leaderOf(person) {
    if (!person || !person.team) return null;
    const live = people.find((x) => x.id !== person.id && x.status === "active"
      && Array.isArray(x.leads) && x.leads.indexOf(person.team) >= 0);
    if (live) return live;
    const lm = leaderMap[person.team];
    if (!lm) return null;
    const p = lm.startsWith("u_") ? byId(lm) : people.find((x) => x.name === lm);
    return p && p.id !== person.id ? p : null;
  }

  // ---- Attendance generation ----
  // Working days of June 2026 up to "today" = the 8th (Mon).
  // June 1 2026 is a Monday.
  const DAYS_ELAPSED = 8;
  function weekday(day) { return (day - 1) % 7; } // 0=Mon ... 6=Sun
  function isWorkday(day) { const w = weekday(day); return w < 5; }

  // ---- Holidays & compensation working days (mirror the backend) ----
  // Holiday name if `iso` (YYYY-MM-DD) is a company holiday, else null. A
  // recurring holiday matches on month/day; a specific one on its date/range.
  function holidayOn(iso, settings) {
    const s = settings || DEFAULT_SETTINGS;
    const m = +iso.slice(5, 7), d = +iso.slice(8, 10);
    for (const h of (s.holidays || [])) {
      if (h.recurring !== false) { if (h.month === m && h.day === d) return h.name || "Holiday"; }
      else if (h.date && h.date <= iso && iso <= (h.dateTo || h.date)) return h.name || "Holiday";
    }
    return null;
  }
  function isCompWork(iso, settings) { return ((settings || DEFAULT_SETTINGS).compWorkdays || []).some((c) => c.workDate === iso); }
  function isCompOff(iso, settings) { return ((settings || DEFAULT_SETTINGS).compWorkdays || []).some((c) => c.offDate === iso); }
  // True if `iso` is a working date, accounting for weekends, holidays and
  // compensation swaps (a comp work-date overrides the weekend; a holiday or a
  // comp off-date overrides a weekday).
  function isWorkingDate(iso, settings) {
    if (isCompWork(iso, settings)) return true;
    const wd = new Date(iso + "T00:00:00").getDay(); // 0=Sun .. 6=Sat
    if (wd === 0 || wd === 6) return false;
    if (holidayOn(iso, settings)) return false;
    if (isCompOff(iso, settings)) return false;
    return true;
  }
  // The label for a non-working day caused by a holiday/comp-off (else null).
  function dayOffLabel(iso, settings) {
    return holidayOn(iso, settings) || (isCompOff(iso, settings) ? "Compensatory day off" : null);
  }

  // Generate a realistic month for one person; returns records + rollups.
  function genMonth(seedKey, profile) {
    const r = rng(seedKey);
    const records = [];
    let fundUsed = 0, permUsed = 0, workMinDeducted = 0, leaveDays = 0;
    for (let day = 1; day <= DAYS_ELAPSED; day++) {
      const iso = "2026-06-" + String(day).padStart(2, "0");
      if (!isWorkingDate(iso, DEFAULT_SETTINGS)) { const lbl = dayOffLabel(iso, DEFAULT_SETTINGS); const w = { day, weekend: true }; if (lbl) w.holiday = lbl; records.push(w); continue; }
      const rec = { day, in: "08:0" + 0, out: "17:30", lateMin: 0, earlyMin: 0, status: "on-time", source: "auto", permission: false, fundHit: 0, workHit: 0 };
      const roll = r();
      // late
      if (roll < (profile.lateRate || 0.3)) {
        const late = Math.round((5 + r() * (profile.maxLate || 55)) / 5) * 5;
        rec.lateMin = late;
        const hh = 8 + Math.floor(late / 60);
        const mm = late % 60;
        rec.in = String(hh).padStart(2, "0") + ":" + String(mm).padStart(2, "0");
        rec.status = "late";
      }
      // occasional early leave
      if (r() < (profile.earlyRate || 0.12)) {
        const early = Math.round((10 + r() * 50) / 5) * 5;
        rec.earlyMin = early;
        const total = 17 * 60 + 30 - early;
        rec.out = String(Math.floor(total / 60)).padStart(2, "0") + ":" + String(total % 60).padStart(2, "0");
        if (rec.status === "on-time") rec.status = "early-leave";
        else rec.status = "late+early";
      }
      const totalAbs = rec.lateMin + rec.earlyMin;
      // permission?
      if (totalAbs > 0 && permUsed < PERMISSIONS_TOTAL && r() < (profile.permRate || 0.4)) {
        rec.permission = true; permUsed++;
        const overFree = Math.max(0, totalAbs - 60);
        const fromFund = Math.min(overFree, Math.max(0, FUND_TOTAL - fundUsed));
        const fromWork = overFree - fromFund;
        rec.fundHit = fromFund; rec.workHit = fromWork;
        fundUsed += fromFund; workMinDeducted += fromWork;
      } else if (totalAbs > 0) {
        // no permission: fund then work (and >60/day straight to work)
        const over60 = Math.max(0, totalAbs - 60);
        const within60 = totalAbs - over60;
        const fromFund = Math.min(within60, Math.max(0, FUND_TOTAL - fundUsed));
        const fromWorkWithin = within60 - fromFund;
        rec.fundHit = fromFund;
        rec.workHit = fromWorkWithin + over60;
        fundUsed += fromFund; workMinDeducted += rec.workHit;
      }
      // a manual-override flag on a couple of days
      if (r() < (profile.manualRate || 0.06)) { rec.source = "manual"; }
      records.push(rec);
    }
    // a leave block
    if (profile.leave) { leaveDays = profile.leaveDays || 1; }
    return {
      records,
      fundUsed: Math.min(fundUsed, FUND_TOTAL),
      fundRemaining: Math.max(0, FUND_TOTAL - fundUsed),
      permUsed,
      permRemaining: PERMISSIONS_TOTAL - permUsed,
      workMinDeducted,
      leaveDays,
    };
  }

  // current logged-in user for the demo
  const ME = byId("u4"); // Do Gia Bao, Unity Engineer
  const meMonth = genMonth(101, { lateRate: 0.55, maxLate: 50, earlyRate: 0.2, permRate: 0.5, manualRate: 0.18, leave: true, leaveDays: 1 });
  // demo: one "morning off → afternoon" day in the current month (late vs afternoon start)
  (function () {
    const rec = meMonth.records.find((r) => r.day === 4 && !r.weekend);
    if (rec) { rec.amOff = true; rec.session = "pm"; rec.lateMin = 10; rec.earlyMin = 0; rec.in = "—"; rec.pmIn = "13:40"; rec.status = "am-off+late"; rec.permission = true; rec.fundHit = 0; rec.workHit = 0; }
  })();

  // company rollup: a stat line per active person
  const ACTIVE = people.filter((p) => p.status === "active");
  const companyStats = ACTIVE.map((p, i) => {
    const m = genMonth(500 + i, {
      lateRate: 0.2 + (i % 5) * 0.08,
      maxLate: 30 + (i % 4) * 15,
      earlyRate: 0.1 + (i % 3) * 0.05,
      permRate: 0.35,
      manualRate: 0.05,
      leave: i % 6 === 0, leaveDays: 1 + (i % 3),
    });
    const lateMinTotal = m.records.reduce((s, r) => s + (r.lateMin || 0), 0);
    const lateDays = m.records.filter((r) => r.lateMin > 0).length;
    return {
      person: p,
      lateMinTotal, lateDays,
      fundRemaining: m.fundRemaining, permRemaining: m.permRemaining,
      workMinDeducted: m.workMinDeducted, leaveDays: m.leaveDays,
      records: m.records,
    };
  });

  // ---- Requests (pending + history) ----
  const REQ_TYPES = {
    // Leave-type tints, synchronized with the Team Calendar (calendar.js ST).
    late: { label: "Late arrival", icon: "schedule", color: "#D6A820" },
    early: { label: "Early leave", icon: "logout", color: "#D65620" },
    leave: { label: "Annual leave", icon: "beach_access", color: "#D6341E" },
    unpaid_leave: { label: "Unpaid leave", icon: "money_off", color: "#8a6d3b" },
    offset: { label: "Leave offset", icon: "currency_exchange", color: "#2a7d36" },
    hourly_leave: { label: "Hourly leave", icon: "directions_walk", color: "#D62C8E" },
    overtime: { label: "Overtime", icon: "more_time", color: "#7b4fd6" },
    offsite: { label: "Offsite work", icon: "public", color: "#0f766e" },
  };
  function reqRow(id, person, type, dateStr, mins, reason, status, decidedBy) {
    return { id, person, type, date: dateStr, mins, reason, status, decidedBy: decidedBy || null,
      submitted: dateStr, approver: leaderOf(person) || HR_PERSON };
  }
  const requests = [
    reqRow("r1", byId("u4"), "late", "Jun 09", 45, "Doctor appointment in the morning.", "pending"),
    reqRow("r2", byId("u5"), "early", "Jun 09", 30, "Family pickup.", "pending"),
    reqRow("r3", byId("u9"), "leave", "Jun 12", null, "Personal day.", "pending"),
    reqRow("r4", byId("u12"), "late", "Jun 10", 60, "Apartment maintenance.", "pending"),
    reqRow("r5", byId("u17"), "early", "Jun 09", 90, "Medical — over 60 min, surplus hits fund.", "pending"),
    reqRow("r6", byId("u4"), "late", "Jun 04", 30, "Traffic on Pham Hung.", "approved", "Nguyen Van Tuan"),
    reqRow("r7", byId("u4"), "leave", "Jun 02", null, "Annual leave.", "approved", "Nguyen Van Tuan"),
    reqRow("r8", byId("u6"), "late", "Jun 03", 25, "Late train.", "rejected", "Tran Thu Ha"),
    reqRow("r9", byId("u22"), "offset", "Jun 30", 80, "Offset 80 deducted min with annual leave.", "pending"),
  ];

  // ---- Pending account activations (Google SSO sign-ups) ----
  const pendingAccounts = people.filter((p) => p.status === "pending").map((p) => ({
    person: p, email: p.name.toLowerCase().replace(/[^a-z]+/g, ".").replace(/^\.|\.$/g, "") + "@widogame.com",
    signedUp: "Jun " + ri(5, 8) + ", 2026 · 09:1" + ri(0, 9),
  }));

  // ---- Policy (regulations, verbatim VN + EN summary) ----
  const POLICY = {
    title: "Quy định về việc đi muộn & về sớm",
    titleEn: "Late arrival & early leave policy",
    updated: "Effective Jun 2026",
    sections: [
      {
        n: "1", vi: "Quỹ thời gian linh hoạt (Quỹ 300 phút)",
        en: "Flexible time fund (300 minutes)",
        body: "Mỗi nhân sự được cấp một quỹ thời gian linh hoạt tối đa 300 phút/tháng dùng chung cho cả việc đi muộn và về sớm. Thời gian vắng mặt sẽ được tự động trừ trực tiếp vào quỹ này (nếu không thuộc diện xin phép ở Mục 2).",
        bullets: [
          "Đi muộn dưới 30 phút: Không cần thông báo trước.",
          "Đi muộn từ 30 đến tối đa 60 phút: Bắt buộc thông báo trước cho Leader.",
          "Về sớm: Bắt buộc thông báo và có sự đồng ý của Leader đối với mọi trường hợp (bất kể thời gian bao lâu).",
        ],
        summaryEn: "Each person gets 300 flexible minutes per month, shared between late arrivals and early leaves. Absence is auto-deducted from this fund unless covered by an approved permission. Under 30 min late needs no notice; 30–60 min must notify the leader in advance; any early leave always needs leader approval.",
      },
      {
        n: "2", vi: "Đặc quyền xin phép (Miễn trừ quỹ & giờ công)",
        en: "Permission privilege (fund & work-hour exemption)",
        body: "Toàn bộ nhân sự được quyền xin phép đi muộn/về sớm tối đa 4 lần/tháng với điều kiện đã thông báo và được Leader phê duyệt.",
        bullets: [
          "Tối đa 60 phút đầu tiên (tổng đi muộn và/hoặc về sớm trong ngày): Không bị trừ vào quỹ 300 phút, không bị trừ giờ công.",
          "Nếu tổng thời gian xin phép quá 60 phút/ngày: Phần dôi ra (từ phút thứ 61) bị trừ vào quỹ 300 phút. Nếu quỹ đã cạn, phần dôi ra bị trừ trực tiếp vào giờ công.",
        ],
        summaryEn: "Up to 4 approved permissions per month. The first 60 minutes in a day are free — no fund or work-hour deduction. Anything beyond 60 min/day spills into the 300-minute fund, then into work hours once the fund is empty.",
      },
      {
        n: "3", vi: "Xử lý vượt giới hạn và quy tắc trừ công",
        en: "Over-limit handling & pay deduction",
        body: "Công ty áp dụng nguyên tắc hạch toán trả lương theo đúng thời gian làm việc thực tế. Việc không tính công (trừ lương) được thực hiện theo số phút vắng mặt thực tế.",
        bullets: [
          "Vượt 60 phút/ngày (không có lý do chính đáng): phần từ phút thứ 61 tự động trừ thẳng vào giờ công.",
          "Vượt quỹ 300 phút/tháng: sau khi cạn quỹ, mọi phút phát sinh đều trừ thẳng vào giờ công.",
          "Cơ chế: Tổng số phút trừ công / 60 = số giờ vắng mặt, nhân với lương theo giờ để cấn trừ.",
        ],
        summaryEn: "Pay follows actual worked time. Beyond 60 min/day without a valid reason, or after the 300-min fund is spent, minutes are deducted straight from work hours. Deducted minutes ÷ 60 × hourly wage = the pay adjustment.",
      },
      {
        n: "4", vi: "Quyền linh hoạt cấn trừ phép năm",
        en: "Annual-leave offset option",
        body: "Trong trường hợp phát sinh thời gian đi muộn/về sớm bị 'trừ công', nhân sự có quyền chủ động đăng ký sử dụng quỹ phép năm để cấn trừ bù vào số giờ/phút vắng mặt, giúp bảo toàn thu nhập.",
        bullets: [
          "Việc cấn trừ phép năm là tự nguyện.",
          "Nhân sự cần chủ động xác nhận yêu cầu với HR/Kế toán trước kỳ chốt công hàng tháng.",
          "Nếu không có yêu cầu, công ty mặc định hạch toán trừ vào giờ công (không hưởng lương).",
        ],
        summaryEn: "If minutes would be deducted from pay, you may voluntarily offset them with annual leave to protect that month's income. This is opt-in: confirm with HR/Accounting before the monthly payroll close, otherwise the default is an unpaid work-hour deduction.",
      },
    ],
  };

  // Whether `me` may act on (approve / reject / suspend) request `r`. In API
  // mode the backend stamps r.canDecide per the approval rules and we trust it;
  // mock mode falls back to a client-side mirror of backend `_can_approve`:
  //   BOD -> anything; nobody approves their own (except BOD); a leader's/BOD's
  //   request -> BOD only; HR -> leave-offset only; a team leader -> their
  //   members' leave / late / early.
  function canActOnRequest(me, r) {
    if (r && r.canDecide != null) return !!r.canDecide;   // backend-authoritative
    if (!me || !r) return false;
    if (me.roleId === "bod") return true;
    const sub = r.person || {};
    if (me.id === sub.id) return false;                   // no self-approval (non-BOD)
    // A team leader's request -> BOD only. A leader is anyone who leads a team
    // (the `leads` list) or carries the leader role, so a fellow team leader can
    // never approve it. Mirrors the backend `_is_leader`.
    const subIsLeader = sub.roleId === "leader" || !!(sub.leads && sub.leads.length);
    if (sub.roleId === "bod" || subIsLeader) return false;
    if (me.roleId === "hr") return r.type === "offset";   // HR -> offset only
    const teams = sub.teams || (sub.team ? [sub.team] : []);
    const leadsTheirTeam = !!(me.leads && teams.some((t) => me.leads.includes(t)));
    return leadsTheirTeam && (r.type === "leave" || r.type === "unpaid_leave" || r.type === "late" || r.type === "early" || r.type === "hourly_leave" || r.type === "overtime" || r.type === "offsite");
  }

  window.WidoData = {
    MONTH_LABEL, CUR_YEAR, CUR_MONTH, FUND_TOTAL, PERMISSIONS_TOTAL, DAYS_ELAPSED,
    TEAMS, PERMISSIONS, ROLES, people, ACTIVE,
    ME, meMonth, companyStats, requests, REQ_TYPES, pendingAccounts, POLICY,
    DEFAULT_SETTINGS,
    // Open clock fix-ups this month (the "Missing clock records" nav badge).
    // The mock has no such worklist; bootstrap.js fills it in API mode.
    missingCount: 0,
    helpers: { byId, teamName, roleName, leaderOf, weekday, isWorkday, effMinutes, effPerms, isOverridden, adjStat, computeMonth, dailyGrantFor, dailyActive, dailyGrantsFor, dayISO, holidayOn, isCompWork, isCompOff, isWorkingDate, dayOffLabel, effAnnual, isAnnualCustom, annualAccrued, computeLeave, backfillEntries, backfillDays, bonusDays, MONTHS_ELAPSED, monthsEmployed2026, isEmployed, currentStart, tenureLabel, fmtDate, reqDateLabel, timesheetMonth, monthMetrics, MONTH_NAMES, shiftFor, shiftForMonth, schedulesForMonth, canActOnRequest, hourlyLeaveMinutes, parseHHMM },
  };
})();
